// ============================================================
// ErrorBoundary.tsx - React 렌더링 오류 경계
// 컴포넌트 트리에서 발생한 오류를 포착해 폴백 UI 표시
// ============================================================

import React from 'react';

interface Props {
  children: React.ReactNode;
  /** 커스텀 폴백 UI. 생략 시 기본 오류 화면 사용. */
  fallback?: React.ReactNode;
  /** 오류 발생 시 호출되는 콜백 */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? '' });
    this.props.onError?.(error, info);
    // 콘솔에 상세 오류 기록
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, componentStack: '' });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return <>{this.props.fallback}</>;

      return (
        <DefaultErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
          onReset={this.handleReset}
        />
      );
    }
    return <>{this.props.children}</>;
  }
}

// ────────────────────────────────────────────────────────────
// 기본 오류 화면
// ────────────────────────────────────────────────────────────
interface FallbackProps {
  error: Error | null;
  componentStack: string;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<FallbackProps> = ({ error, componentStack, onReset }) => {
  const [showDetail, setShowDetail] = React.useState(false);

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50 p-8 min-h-[200px]">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
        {/* 헤더 */}
        <div className="bg-red-50 border-b border-red-100 px-5 py-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-red-500" fill="none"
              stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-bold text-red-700">문제가 발생했습니다</h2>
            <p className="text-xs text-red-400 mt-0.5">이 화면을 렌더링하는 중 오류가 발생했습니다.</p>
          </div>
        </div>

        {/* 오류 메시지 */}
        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-gray-500 mb-1">오류 메시지</p>
              <p className="text-sm text-gray-800 font-mono break-words">{error.message}</p>
            </div>
          )}

          {/* 상세 정보 토글 */}
          {componentStack && (
            <div>
              <button
                onClick={() => setShowDetail((v) => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
              >
                <svg
                  viewBox="0 0 24 24"
                  className={`w-3 h-3 transition-transform ${showDetail ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" strokeWidth={2}
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
                {showDetail ? '상세 숨기기' : '컴포넌트 스택 보기'}
              </button>
              {showDetail && (
                <pre className="mt-2 text-[10px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-40 leading-relaxed">
                  {componentStack.trim()}
                </pre>
              )}
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onReset}
              className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-xl transition-colors"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
